import { Button } from "@/components/ui/button";
import AdminPrivateLayout from "@/layouts/AdminPrivateLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, Users } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import HistoricalClasses from "@/components/HistoricalClasses";
import UpcomingClasses from "@/components/UpcomingClasses";
import ClassForm from "@/components/ClassForm";
import { getRequestOptions, getCurrentStudio } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Dashboard = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [activeTab, setActiveTab] = useState("upcoming");
  const upcomingClassesRef = useRef<{ handleRefresh: () => void } | null>(null);

  const stats = [
    {
      title: t('dashboard.stats.totalClasses'),
      value: "24",
      icon: Calendar,
    },
    {
      title: t('dashboard.stats.totalStudents'),
      value: "156",
      icon: Users,
    },
  ];

  const onSubmit = async (values: any) => {
    console.log("Form Values:", values); // Logs the form values from ClassForm
    setIsCreateOpen(false);
    setIsEditOpen(false);
    try {
      let fetchURL = `${import.meta.env.VITE_BOOKING_CLASSES_ENDPOINT}`;
      let method = "POST"; // Default to POST for creating new class
      if (values.id) {
        method = "PUT"; // Change to PUT for updating an existing class
      }

      const response = await fetch(fetchURL, getRequestOptions(method, JSON.stringify(values), "json"));
      if (!response.ok) {
        throw new Error("Failed to add or update the class");
      }

      const data = await response.json();
      console.log("Response Data:", data); // Optional: Check response from API
      toast({
        title: t("classForm.createSuccess.title"),
        description: t("classForm.createSuccess.description"),
        duration: 5000,
        className: "bg-green-500 text-white border-green-600",
      });
      // Trigger refresh for upcoming classes
      if (upcomingClassesRef.current) {
        upcomingClassesRef.current.handleRefresh();
      }
    } catch (error) {
      console.error("Error adding class:", error);
    }
  };

  const onCancel = async (id: string) => {
    console.log("Form id:", id); // Logs the form values from ClassForm
    setIsCreateOpen(false);
    setIsEditOpen(false);
    try {
      let fetchURL = `${import.meta.env.VITE_BOOKING_CLASSES_ENDPOINT}` + '/cancel';
      const payload = { id };
      const response = await fetch(fetchURL, getRequestOptions("POST", JSON.stringify(payload), "json"));
      if (!response.ok) {
        throw new Error("Failed to add or update the class");
      }

      const data = await response.json();
      console.log("Response Data:", data); // Optional: Check response from API
      toast({
        title: t("classForm.cancelSuccess.title"),
        description: t("classForm.cancelSuccess.description"),
        duration: 5000,
        className: "bg-green-500 text-white border-green-600",
      });
      // Trigger refresh for upcoming classes
      if (upcomingClassesRef.current) {
        upcomingClassesRef.current.handleRefresh();
      }
    } catch (error) {
      console.error("Error adding class:", error);
    }
  };

  const onDelete = async (id: string) => {
    console.log("Form id:", id); // Logs the form values from ClassForm
    setIsCreateOpen(false);
    setIsEditOpen(false);
    try {
      let fetchURL = `${import.meta.env.VITE_BOOKING_CLASSES_ENDPOINT}`;
      const payload = { id };
      const response = await fetch(fetchURL, getRequestOptions("DELETE", JSON.stringify(payload), "json"));
      if (!response.ok) {
        throw new Error("Failed to add or update the class");
      }

      const data = await response.json();
      console.log("Response Data:", data); // Optional: Check response from API
      toast({
        title: t("classForm.deleteSuccess.title"),
        description: t("classForm.deleteSuccess.description"),
        duration: 5000,
        className: "bg-green-500 text-white border-green-600",
      });
      // Trigger refresh for upcoming classes
      if (upcomingClassesRef.current) {
        upcomingClassesRef.current.handleRefresh();
      }
    } catch (error) {
      console.error("Error adding class:", error);
    }
  };

  const handleEdit = (classItem: any) => {
    setSelectedClass(classItem);
    setIsEditOpen(true);
  };

  useEffect(() => {
    document.title = 'Admin Dashboard - ' + getCurrentStudio()?.name;
  }, []);

  return (
    <AdminPrivateLayout>
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {stats.map((stat) => (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <stat.icon className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          ))}
        </div> */}

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">&nbsp;</h2>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-secondary w-full sm:w-auto">
                {t('dashboard.createNewClass')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('dashboard.createClass.title')}</DialogTitle>
                <DialogDescription>
                  {t('dashboard.createClass.description')}
                </DialogDescription>
              </DialogHeader>
              <ClassForm onSubmit={onSubmit} submitLabel={t('dashboard.createClass.create')} />
            </DialogContent>
          </Dialog>
        </div>

        <Tabs
          defaultValue="upcoming"
          className="w-full space-y-8"
          onValueChange={setActiveTab}
        >
          <TabsList className="w-full max-w-md mx-auto grid grid-cols-2 h-12 items-stretch bg-muted/50 p-1 rounded-lg">
            <TabsTrigger
              value="upcoming"
              className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-md transition-all"
            >
              {t('dashboard.upcomingClasses')}
            </TabsTrigger>
            <TabsTrigger
              value="historical"
              className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-md transition-all"
            >
              {t('dashboard.historicalClasses')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            <UpcomingClasses ref={upcomingClassesRef} onEdit={handleEdit} />
          </TabsContent>

          <TabsContent value="historical">
            {activeTab === "historical" && <HistoricalClasses />}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dashboard.editClass.title')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.editClass.description')}
            </DialogDescription>
          </DialogHeader>
          <ClassForm
            onSubmit={onSubmit}
            onCancel={onCancel}
            onDelete={onDelete}
            defaultValues={selectedClass}
            submitLabel={t('dashboard.editClass.update')}
          />
        </DialogContent>
      </Dialog>
    </AdminPrivateLayout>
  );
};

export default Dashboard;